/**
 * Exact arithmetic on the API's decimal money STRINGS.
 *
 * The portal must sometimes add figures the API did not pre-total (a ledger's
 * running balance). Doing that with `parseFloat` would drift by cents on long
 * ranges, which is exactly what the DTO contract warns about — so the maths
 * happens in scaled integers (BigInt) and comes back out as a string.
 *
 * Every function returns `null` when an input is not a plain decimal, so the UI
 * can show "—" instead of a wrong number.
 */
const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

interface Scaled {
  units: bigint;
  scale: number;
}

function parse(value: string | null | undefined): Scaled | null {
  const match = value === null || value === undefined ? null : DECIMAL.exec(value.trim());
  if (!match) return null;
  const fraction = match[3] ?? '';
  const digits = `${match[2]}${fraction}`;
  const units = BigInt(digits) * (match[1] === '-' ? -1n : 1n);
  return { units, scale: fraction.length };
}

function rescale(value: Scaled, scale: number): bigint {
  return value.units * 10n ** BigInt(scale - value.scale);
}

function format(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale === 0 ? '' : `.${digits.slice(digits.length - scale)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
}

/** Exact sum of decimal strings, at the widest scale of its inputs (min 2). */
export function sumAmounts(values: readonly (string | null | undefined)[]): string | null {
  const parsed: Scaled[] = [];
  for (const value of values) {
    const item = parse(value);
    if (item === null) return null;
    parsed.push(item);
  }
  const scale = Math.max(2, ...parsed.map((item) => item.scale));
  let total = 0n;
  for (const item of parsed) total += rescale(item, scale);
  return format(total, scale);
}

/** Exact `a - b`. */
export function subtractAmounts(a: string | null | undefined, b: string | null | undefined): string | null {
  const left = parse(a);
  const right = parse(b);
  if (left === null || right === null) return null;
  const scale = Math.max(2, left.scale, right.scale);
  return format(rescale(left, scale) - rescale(right, scale), scale);
}
